import { useState } from "react";
import api from "../services/api";
import Swal from "sweetalert2";

export default function useCart() {
  const [cart, setCart] = useState([]);

  const addProductWithPresentation = (product, presentation) => {
    const price = Number(presentation.precio_venta);
    if (isNaN(price)) return;

    setCart((prev) => {
      const uniqueKey = `${product.id}-${presentation.id}`;
      const found = prev.find((p) => p.uniqueKey === uniqueKey);

      if (found) {
        const newQty = found.cantidad + 1;

        if (presentation.tipo_presentacion === "variante") {
          const availableStock = presentation.stock_actual || presentation.stock_independiente || 0;
          if (newQty > availableStock) {
            Swal.fire({
              icon: "warning",
              title: "Insufficient stock",
              text: `Only ${availableStock} units available of "${presentation.descripcion}"`,
              confirmButtonText: "Got it",
            });
            return prev;
          }
        }

        return prev.map((p) =>
          p.uniqueKey === uniqueKey
            ? { ...p, cantidad: newQty, subtotal: newQty * p.precio_unitario }
            : p
        );
      }

      const newItem = {
        uniqueKey,
        product_id: product.id,
        presentation_id: presentation.id,
        codigo: product.codigo || `PROD-${product.id}`,
        nombre: `${product.nombre} - ${presentation.descripcion}`,
        precio_unitario: price,
        cantidad: 1,
        subtotal: price,
        is_taxable: Boolean(product.is_taxable),
        factor_conversion: presentation.factor_conversion,
        tipo_presentacion: presentation.tipo_presentacion || "empaque",
        stock_disponible: presentation.stock_actual || null,
      };

      return [...prev, newItem];
    });
  };

  const addProductByCode = async (rawQuery) => {
    try {
      if (!rawQuery) return;
      const query = rawQuery.trim();
      let productData = null;

      try {
        const resCode = await api.get(`/products/code/${query}`);
        productData = resCode.data;
      } catch (err) {
        const resAll = await api.get("/products");
        const allProducts = resAll.data.data ?? resAll.data;
        const matches = allProducts.filter((p) =>
          p.nombre.toLowerCase().includes(query.toLowerCase())
        );

        if (matches.length === 0) throw new Error("Product not found");

        if (matches.length === 1) {
          const singleProduct = matches[0];
          const resCode = await api.get(`/products/code/${singleProduct.codigo}`);
          productData = resCode.data;
        } else {
          const selected = await Swal.fire({
            title: "Select a product",
            input: "select",
            inputOptions: matches.reduce((acc, p) => {
              acc[p.id] = `${p.codigo || "NO-CODE"} - ${p.nombre}`;
              return acc;
            }, {}),
            inputPlaceholder: "Choose a product",
            showCancelButton: true,
          });

          if (!selected.isConfirmed) return;

          const selectedProduct = matches.find((p) => p.id === Number(selected.value));
          const resCode = await api.get(`/products/code/${selectedProduct.codigo}`);
          productData = resCode.data;
        }
      }

      if (productData.has_presentations) {
        const { product, presentations } = productData;

        const presentationOptions = presentations.reduce((acc, pres) => {
          let label = `${pres.descripcion} - $${pres.precio_venta.toFixed(2)}`;
          if (pres.tipo_presentacion === "variante") {
            const stock = pres.stock_actual || pres.stock_independiente || 0;
            label += ` (Stock: ${stock})`;
          }
          acc[pres.id] = label;
          return acc;
        }, {});

        const result = await Swal.fire({
          title: `📦 ${product.nombre}`,
          html: `<div style="margin-bottom: 8px; color: #718096; font-size: 14px;">Use the ↑↓ arrows and press Enter</div>`,
          input: "select",
          inputOptions: presentationOptions,
          inputValue: presentations[0].id,
          showCancelButton: true,
          confirmButtonText: "✓ Add",
          cancelButtonText: "✕ Cancel",
          didOpen: () => {
            const select = Swal.getInput();
            if (select) setTimeout(() => select.focus(), 100);
          },
          customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-secondary",
          },
        });

        if (!result.isConfirmed) return;

        const selectedPresentation = presentations.find((p) => p.id === Number(result.value));

        if (selectedPresentation.tipo_presentacion === "variante") {
          const availableStock =
            selectedPresentation.stock_actual || selectedPresentation.stock_independiente || 0;
          if (availableStock <= 0) {
            Swal.fire({
              icon: "error",
              title: "Out of stock",
              text: `No units available of "${selectedPresentation.descripcion}"`,
              confirmButtonText: "Got it",
            });
            return;
          }
        }

        addProductWithPresentation(product, selectedPresentation);
        return;
      }

      const price = Number(productData.precio_venta || productData.precio);
      if (isNaN(price)) throw new Error("Invalid price");

      setCart((prev) => {
        const found = prev.find((p) => p.product_id === productData.id && !p.presentation_id);

        if (found) {
          const newQty = found.cantidad + 1;
          return prev.map((p) =>
            p.product_id === productData.id && !p.presentation_id
              ? { ...p, cantidad: newQty, subtotal: newQty * p.precio_unitario }
              : p
          );
        }

        return [
          ...prev,
          {
            uniqueKey: `${productData.id}-simple`,
            product_id: productData.id,
            presentation_id: null,
            codigo: productData.codigo_barra || productData.codigo || `PROD-${productData.id}`,
            nombre: productData.nombre,
            precio_unitario: price,
            cantidad: 1,
            subtotal: price,
            is_taxable: Boolean(productData.is_taxable),
            tipo_presentacion: null,
            stock_disponible: null,
          },
        ];
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error", error.message || "Product not found", "error");
    }
  };

  const addProduct = async (product) => {
    try {
      const resCode = await api.get(`/products/code/${product.codigo}`);
      const productData = resCode.data;

      if (productData.has_presentations) {
        const { product: prod, presentations } = productData;

        const presentationOptions = presentations.reduce((acc, pres) => {
          let label = `${pres.descripcion} - $${pres.precio_venta.toFixed(2)}`;
          if (pres.tipo_presentacion === "variante") {
            const stock = pres.stock_actual || pres.stock_independiente || 0;
            label += ` (Stock: ${stock})`;
          }
          acc[pres.id] = label;
          return acc;
        }, {});

        const result = await Swal.fire({
          title: `📦 ${prod.nombre}`,
          html: `<div style="margin-bottom: 8px; color: #718096; font-size: 14px;">Use the ↑↓ arrows and press Enter</div>`,
          input: "select",
          inputOptions: presentationOptions,
          inputValue: presentations[0].id,
          showCancelButton: true,
          confirmButtonText: "✓ Add",
          cancelButtonText: "✕ Cancel",
          didOpen: () => {
            const select = Swal.getInput();
            if (select) setTimeout(() => select.focus(), 100);
          },
          customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-secondary",
          },
        });

        if (!result.isConfirmed) return;

        const selectedPresentation = presentations.find((p) => p.id === Number(result.value));

        if (selectedPresentation.tipo_presentacion === "variante") {
          const availableStock =
            selectedPresentation.stock_actual || selectedPresentation.stock_independiente || 0;
          if (availableStock <= 0) {
            Swal.fire({
              icon: "error",
              title: "Out of stock",
              text: `No units available of "${selectedPresentation.descripcion}"`,
              confirmButtonText: "Got it",
            });
            return;
          }
        }

        addProductWithPresentation(prod, selectedPresentation);
        return;
      }

      const price = Number(product.precio_venta || product.precio);
      if (isNaN(price)) return;

      setCart((prev) => {
        const found = prev.find((p) => p.product_id === product.id && !p.presentation_id);

        if (found) {
          const newQty = found.cantidad + 1;
          return prev.map((p) =>
            p.product_id === product.id && !p.presentation_id
              ? { ...p, cantidad: newQty, subtotal: newQty * p.precio_unitario }
              : p
          );
        }

        return [
          ...prev,
          {
            uniqueKey: `${product.id}-simple`,
            product_id: product.id,
            presentation_id: null,
            codigo: product.codigo || `PROD-${product.id}`,
            nombre: product.nombre,
            precio_unitario: price,
            cantidad: 1,
            subtotal: price,
            is_taxable: Boolean(product.is_taxable),
            tipo_presentacion: null,
            stock_disponible: null,
          },
        ];
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Could not add the product", "error");
    }
  };

  const updateQuantity = (uniqueKey, cantidad) => {
    setCart((prev) =>
      prev.map((p) => {
        if (p.uniqueKey !== uniqueKey) return p;
        const qty = cantidad === "" ? "" : Number(cantidad);

        if (p.tipo_presentacion === "variante" && p.stock_disponible !== null && qty > p.stock_disponible) {
          Swal.fire({
            icon: "warning",
            title: "Insufficient stock",
            text: `Only ${p.stock_disponible} units available`,
            confirmButtonText: "Got it",
          });
          return p;
        }

        return {
          ...p,
          cantidad: qty,
          subtotal: qty === "" ? 0 : qty * p.precio_unitario,
        };
      })
    );
  };

  const removeItem = (uniqueKey) => {
    setCart((prev) => prev.filter((p) => p.uniqueKey !== uniqueKey));
  };

  const subtotal = cart.reduce((sum, p) => sum + p.subtotal, 0);
  const taxableSubtotal = cart.filter((p) => p.is_taxable).reduce((sum, p) => sum + p.subtotal, 0);
  const exemptSubtotal = cart.filter((p) => !p.is_taxable).reduce((sum, p) => sum + p.subtotal, 0);

  const buildPayload = (clientId) => ({
    client_id: clientId || null,
    fecha_venta: new Date().toISOString().slice(0, 10),
    details: cart.map((p) => ({
      product_id: p.product_id,
      presentation_id: p.presentation_id || null,
      cantidad: p.cantidad,
      precio_unitario: p.precio_unitario,
    })),
  });

  const clearCart = () => setCart([]);

  return {
    cart,
    setCart,
    subtotal,
    taxableSubtotal,
    exemptSubtotal,
    addProduct,
    addProductByCode,
    addProductWithPresentation,
    updateQuantity,
    removeItem,
    buildPayload,
    clearCart,
  };
}